import {Injectable} from '@angular/core';
import {IAdapter} from '@drl/core/api/adapters/i-adapter';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import {keys} from 'lodash-es';
import * as moment from 'moment';
import {PrescriptionAuditLog} from '../models/prescription-audit-log.model';

@Injectable()
export class PrescriptionAuditLogsAdapter
  implements IAdapter<PrescriptionAuditLog>
{
  constructor(private readonly translateSvc: IpsTranslateService) {}

  adaptToModel(resp: any): PrescriptionAuditLog {
    const log = new PrescriptionAuditLog();
    if (resp) {
      log.id = resp.id;
      log.logType = resp.logType;
      log.operationName = resp.operationName;
      log.operationTime = resp.operationTime;
      log.dateString = moment(resp.operationTime).format(
        'MMMM DD, YYYY hh:mm a',
      );
      log.userId = resp.userId;
      log.before = resp.before;
      log.after = resp.after;
      log.entityId = resp.entityId;
      this.getActionText(log);
    }

    return log;
  }

  adaptFromModel(data: Partial<PrescriptionAuditLog>): any {
    return data;
  }

  getActionText(log: PrescriptionAuditLog) {
    log.actions = [];
    if (log.operationName === 'INSERT') {
      if (log.logType === 'PRESCRIPTION_LOGS') {
        log.actions.push({
          actionText: this.translateSvc.translate.instant(
            'addedPrescriptionMsg',
          ),
          actionField: '',
        });
        log.showLog = true;
      } else {
        log.actions.push({
          actionText: this.translateSvc.translate.instant('addedMedicineMsg'),
          actionField: log.after && log.after['name'],
        });
        log.showLog = true;
      }
    } else {
      this.getUpdates(log);
    }
  }

  getUpdates(log: PrescriptionAuditLog) {
    const diff = this.compare(log.before, log.after);
    if (log.logType === 'PRESCRIPTION_LOGS' && diff[0] === 'Deactivated') {
      log.actions.push({
        actionText: this.translateSvc.translate.instant(
          'deactivatedPrescriptionMsg',
        ),
        actionField: '',
      });
      log.showLog = true;
    } else if (log.logType === 'PRESCRIPTION_LOGS' && diff[0] === 'Activated') {
      log.actions.push({
        actionText: this.translateSvc.translate.instant(
          'activatedPrescriptionMsg',
        ),
        actionField: '',
      });
      log.showLog = true;
    } else if (log.logType === 'PRESCRIPTION_LOGS' && diff[0] === 'Deleted') {
      log.actions.push({
        actionText: this.translateSvc.translate.instant(
          'deletedPrescriptionMsg',
        ),
        actionField: '',
      });
      log.showLog = true;
    } else if (
      log.logType === 'PRESCRIPTION_MEDICINE_LOGS' &&
      diff[0] === 'Deleted'
    ) {
      log.actions.push({
        actionText: this.translateSvc.translate.instant('deletedMedicineMsg'),
        actionField: log.after && log.after['name'],
      });
      log.showLog = true;
    } else if (log.logType === 'PRESCRIPTION_LOGS' && diff.length > 0) {
      diff.forEach(field => {
        log.actions.push({
          actionText: this.translateSvc.translate.instant(
            'updatedPrescriptionMsg',
          ),
          actionField: field,
        });
      });

      log.showLog = true;
    } else if (
      log.logType === 'PRESCRIPTION_MEDICINE_LOGS' &&
      diff.length > 0
    ) {
      diff.forEach(field => {
        log.actions.push({
          actionText: this.translateSvc.translate.instant(
            'updatedMedicineMsg',
            {medName: log.after && log.after['name']},
          ),
          actionField: field,
        });
      });
      log.showLog = true;
    } else {
      // Do nothing
    }
  }

  compare(before: object, after: object): string[] {
    let difference = [];
    const beforeKeys = keys(before);

    for (const key of beforeKeys) {
      if (before[key] !== after[key]) {
        if (key === 'is_active' && after[key] === false) {
          difference = ['Deactivated'];
          break;
        } else if (key === 'is_active' && after[key] === true) {
          difference = ['Activated'];
          break;
        } else if (key === 'deleted' && after[key] === true) {
          difference = ['Deleted'];
          break;
        } else if (
          [
            'external_id',
            'appointment_id',
            'medicine_ext_id',
            'modified_on',
            'modified_by',
            'created_on',
            'created_by',
          ].includes(key)
        ) {
          continue;
        } else {
          difference.push(this.translateSvc.translate.instant(key));
        }
      }
    }
    return difference;
  }
}
